import { useState } from "react";
import { sendFeedback } from "../api";

/**
 * App-wide floating "Feedback" button (bottom-left, so it never collides with the bottom-right
 * getting-started bubble). Any signed-in user can leave a note; the CURRENT page path is sent along so
 * operators see exactly where the feedback came from. Rendered from App only when signed in.
 */
export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);

  const page = window.location.pathname + window.location.search;

  async function submit() {
    const text = msg.trim();
    if (text === "") return;
    setState("sending");
    setErr(null);
    try {
      await sendFeedback(text, page);
      setState("sent");
      setMsg("");
      // Auto-close the confirmation so the widget tucks itself away again.
      window.setTimeout(() => {
        setOpen(false);
        setState("idle");
      }, 2200);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't send — please try again.");
      setState("error");
    }
  }

  return (
    <div className="feedback-widget">
      {open && (
        <div className="feedback-panel" role="dialog" aria-label="Send feedback">
          {state === "sent" ? (
            <div className="feedback-done">
              <strong>Thanks! 🙏</strong>
              <p className="muted">Your note reached our team.</p>
            </div>
          ) : (
            <>
              <div className="feedback-head">
                <strong>Send feedback</strong>
                <button className="feedback-x" aria-label="Close" onClick={() => setOpen(false)}>
                  ×
                </button>
              </div>
              <p className="muted feedback-sub">
                A bug, an idea, or anything odd on this page — we read every note.
              </p>
              <textarea
                className="feedback-text"
                rows={4}
                autoFocus
                placeholder="What's on your mind?"
                value={msg}
                maxLength={4000}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit();
                }}
              />
              {err && <div className="err">{err}</div>}
              <div className="feedback-foot">
                <span className="muted feedback-page" title={page}>
                  {window.location.pathname}
                </span>
                <button
                  className="btn sm"
                  disabled={state === "sending" || msg.trim() === ""}
                  onClick={() => void submit()}
                >
                  {state === "sending" ? "Sending…" : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <button
        className={`feedback-launcher${open ? " is-open" : ""}`}
        onClick={() => {
          setOpen((o) => !o);
          setState("idle");
          setErr(null);
        }}
        aria-expanded={open}
        title="Send feedback"
      >
        {open ? "Close" : "Feedback"}
      </button>
    </div>
  );
}
